// src/components/RevenueDonutChart.tsx
import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import {
  PieChart,
  Pie,
  Label,
} from "recharts";
import { formatNumberLocale } from "@/utils/helpers";
import { ChartConfig, ChartTooltip, ChartContainer, ChartTooltipContent, ChartLegendContent, ChartLegend } from "@/components/ui/chart";
import { TenderState } from "@/graphql/__generated__/graphql";

// Define the structure of tenderRevenueStats
export interface TenderRevenueStats {
  totalRevenue: string;
  wonTendersRevenue: string;
  lostTendersRevenue: string;
  monthlyRevenue: string[];
}

interface RevenueDonutChartProps {
  tenderRevenueStats: TenderRevenueStats;
}

/**
 * RevenueDonutChart component displays a doughnut chart representing the revenue statistics
 * of won and lost tenders.
 *
 * @param {RevenueDonutChartProps} props - The properties for the component.
 * @param {Object} props.tenderRevenueStats - The revenue statistics for tenders.
 * @param {number} props.tenderRevenueStats.wonTendersRevenue - The revenue from won tenders.
 * @param {number} props.tenderRevenueStats.lostTendersRevenue - The revenue from lost tenders.
 *
 * @returns {JSX.Element} The rendered RevenueDonutChart component.
 *
 * @example
 * <RevenueDonutChart tenderRevenueStats={{ wonTendersRevenue: 5000, lostTendersRevenue: 2000 }} />
 */
const RevenueDonutChart: React.FC<RevenueDonutChartProps> = ({
  tenderRevenueStats,
}: RevenueDonutChartProps): JSX.Element => {
  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components"]);

  // Extract and parse revenue values
  const wonRevenue = Number(tenderRevenueStats?.wonTendersRevenue) || 0;
  const lostRevenue = Number(tenderRevenueStats?.lostTendersRevenue) || 0;

  // Check if there is any data to display
  const hasData = wonRevenue > 0 || lostRevenue > 0;

  const chartConfig = {
    revenue: {
      label: t("common:revenue"),
    },
    [TenderState.Won]: {
      label: t("common:tenderstate.won"),
      color: "#CCE0AC",
    },
    [TenderState.Lost]: {
      label: t("common:tenderstate.lost"),
      color: "#FF8A8A",
    },
  } satisfies ChartConfig;

  const chartData = [{
    status: TenderState.Won,
    revenue: wonRevenue,
    fill: chartConfig[TenderState.Won].color,
  }, {
    status: TenderState.Lost,
    revenue: lostRevenue,
    fill: chartConfig[TenderState.Lost].color,
  }];

  const totalRevenue = wonRevenue + lostRevenue;

  return (
    <div className="w-full p-2 flex flex-col max-h-[400px] lg:max-h-none lg:h-80 flex-1">
      <div className="w-full text-center">
        <h1 className="font-semibold">
          {t("revenuedoughnutchart.title")}
        </h1>
      </div>
      {
        !hasData ? (
          <div className="w-full text-center lg:-mt-6 h-full justify-center items-center flex">
            <p className="text-sm text-slate-500">{t("common:nodataavailable")}</p>
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="mx-auto min-h-[260px] max-h-[260px] w-full"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel currency />}
              />
              <Pie
                data={chartData}
                dataKey="revenue"
                nameKey="status"
                innerRadius={60}
                strokeWidth={5}
              >
                <Label
                  position="center"
                  value={formatNumberLocale(intl, totalRevenue, "currency")}
                  className="fill-foreground text-xs font-bold -translate-y-2"
                />
                <Label
                  position="center"
                  value={t("revenuedoughnutchart.revenue")}
                  className="fill-muted-foreground translate-y-4"
                />
              </Pie>
              <ChartLegend
                content={(
                  <ChartLegendContent
                    nameKey="status"
                    className="-translate-y-4 flex-wrap gap-x-4 gap-y-2 max-h-[100px] overflow-x-auto flex flex-row"
                  />
                )}
              />
            </PieChart>
          </ChartContainer>
        )}
    </div>
  );
};

export default RevenueDonutChart;
